import { useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import RepeatIcon from "@mui/icons-material/Repeat";
import Drawer from "../../components/ui/Drawer";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import {
  useCreateMuzakereMutation,
  useGetManagerAssessmentQuery,
  useSetAssessmentMutation,
} from "../../api/muzakereApi";
import { useCreatePackageMutation, useGetLibraryAccessQuery, useGetPackageMutation } from "../../api/libraryApi";
import { todayString } from "../../lib/muzakereWeek";
import { parseLocalDate } from "../../lib/dateUtils";
import { SoftButton, cls } from "../hatim/soft/SoftKit";
import QuestionsSection from "./QuestionsSection";
import PackagePickerDialog from "./PackagePickerDialog";
import SaveAsPackageDialog from "./SaveAsPackageDialog";
import { cloneQuestions, questionsIssue, issueText, shouldOfferSaveAsPackage, toPayload } from "./questionRules";
import {
  EMPTY_FORM,
  MUZAKERE_DESCRIPTION_MAX,
  MUZAKERE_TITLE_MAX,
  REPEAT_LIMITS,
  applyPackage,
  buildCreateBody,
  buildSetAssessmentBody,
  canAdvance,
  minMuzakereDate,
  monthDayOf,
  nextMonthDay,
  nextWeekday,
  repeatDates,
  setRepeat,
  singleIfQuestions,
  weekdayOf,
  wizardSteps,
} from "./muzakereWizardRules";

const WEEKDAYS = [
  [1, "Pzt"],
  [2, "Sal"],
  [3, "Çar"],
  [4, "Per"],
  [5, "Cum"],
  [6, "Cmt"],
  [7, "Paz"],
];

const inputCls = `w-full rounded-xl border border-terakkiBorder bg-terakkiSurface px-3 py-2 text-sm text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`;

const chipCls = (active) =>
  `rounded-full border text-sm transition-colors ${
    active
      ? "bg-terakkiPrimaryDark border-terakkiPrimaryDark text-white dark:bg-[#8FC9CA] dark:border-[#8FC9CA] dark:text-[#123B3D]"
      : `border-terakkiBorder dark:border-white/[0.14] ${cls.title} ${cls.rowHover}`
  }`;

/** "30 Eylül 2026 Çarşamba" */
export function longDate(ymd) {
  const d = parseLocalDate(ymd);
  if (!d) return "";
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" });
}

const shortDate = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-");
  return y && m && d ? `${d}.${m}.${y}` : "";
};

/** Adımın başlığı ve açıklaması (çeviri anahtarları). */
function stepText(step, form, questionsOnly) {
  if (step === "questions") return questionsOnly ? ["mzw_questions_only", "mzw_questions_only_desc"] : ["mzw_questions", "mzw_questions_desc"];
  if (step === "info") return ["mzw_info", "mzw_info_desc"];
  if (step === "repeat") return ["mzw_repeat", "mzw_repeat_desc"];
  if (step === "summary") return ["mzw_summary", "mzw_summary_desc"];
  if (form.questions.length > 0) return ["mzw_date", "mzw_date_single_desc"];
  if (form.repeat === "weekly") return ["mzw_weekday", "mzw_weekday_desc"];
  if (form.repeat === "monthly") return ["mzw_monthday", "mzw_monthday_desc"];
  return ["mzw_date", "mzw_date_desc"];
}

/** Tekrarın okunur özeti: "Her hafta Çarşamba · 4 kez · 30.09.2026 · 21.10.2026". */
export function repeatSummary(form, t) {
  const dates = repeatDates(form.date, form.repeat, form.count);
  if (!dates.length) return "";
  const first = parseLocalDate(form.date);
  const every =
    form.repeat === "weekly"
      ? `${t("mzw_every_week")} ${first.toLocaleDateString("tr-TR", { weekday: "long" })}`
      : `${t("mzw_every_month")} ${first.getDate()}.`;
  return `${every} · ${form.count} ${t("mzw_times")} · ${shortDate(dates[0])} · ${shortDate(dates[dates.length - 1])}`;
}

/**
 * Müzakere Ekle (mobil MuzakereWizard): sağdan açılan çekmecede adım adım;
 * paket ve sorular, başlık ve açıklama, tekrar (sorusuzsa), tarih ve özet.
 * Eklenen müzakere düzenlenmez. questionsFor verilirse ({ id, title }) var
 * olan müzakerenin yalnız soruları düzenlenir (değerlendirme başlamadan).
 */
export default function MuzakereWizardDrawer({ open, groupId, questionsFor = null, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const questionsOnly = !!questionsFor;
  const today = todayString();
  const [form, setForm] = useState(EMPTY_FORM);
  const [ready, setReady] = useState(!questionsOnly);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState(null);
  // Oluşturma isteği cevapsız kaldı: sunucuda eklenmiş olabilir, yeniden gönderilmez.
  const [uncertain, setUncertain] = useState(false);
  const [packageTitle, setPackageTitle] = useState(null);
  // Seçili paketin (ya da yeni kaydedilen halin) soru kopyası; değişiklik bununla ölçülür.
  const [packageQuestions, setPackageQuestions] = useState(null);
  const [packageSaved, setPackageSaved] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const inFlight = useRef(false);

  const { data: accessData } = useGetLibraryAccessQuery(undefined, { skip: !open });
  const canUseLibrary = Boolean(accessData?.data?.canUse);
  // currentData: yalnız bu müzakerenin cevabı (önceki müzakerenin sonucu değil).
  const {
    currentData: assessmentData,
    isError: loadFailed,
    isFetching: loadingQuestions,
  } = useGetManagerAssessmentQuery(
    { muzakereId: questionsFor?.id },
    { skip: !open || !questionsOnly, refetchOnMountOrArgChange: true }
  );
  const [createMuzakere, { isLoading: creating }] = useCreateMuzakereMutation();
  const [setAssessment, { isLoading: savingQuestions }] = useSetAssessmentMutation();
  const [createPackage] = useCreatePackageMutation();
  const [getPackage] = useGetPackageMutation();
  const saving = creating || savingQuestions;

  // Her açılışta baştan: yeni müzakerede boş form; soru düzenlemede sorular yüklenince.
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setReady(!questionsOnly);
    setStepIndex(0);
    setError(null);
    setUncertain(false);
    setPackageTitle(null);
    setPackageQuestions(null);
    setPackageSaved(false);
    inFlight.current = false;
  }, [open, questionsOnly, questionsFor?.id]);

  useEffect(() => {
    if (!open || !questionsOnly || ready || loadingQuestions) return;
    const d = assessmentData?.data;
    if (!d || (d.muzakereId && d.muzakereId !== questionsFor?.id)) return;
    const questions = cloneQuestions(d.questions);
    const packageId = d.packageId || null;
    setForm({ ...EMPTY_FORM, title: questionsFor?.title || "", packageId, questions });
    setPackageQuestions(packageId ? cloneQuestions(d.questions) : null);
    setReady(true);
    // Seçili paketin adı (paket kaldırılmış ya da kilitliyse gösterilmez).
    if (packageId) {
      getPackage({ packageId }).then((r) => {
        const title = r.data?.data?.package?.title;
        if (title) setPackageTitle(title);
      });
    }
  }, [open, questionsOnly, ready, loadingQuestions, assessmentData, questionsFor?.id, questionsFor?.title, getPackage]);

  const steps = wizardSteps({ questionsOnly, hasQuestions: form.questions.length > 0 });
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const isLast = stepIndex >= steps.length - 1;
  const [titleKey, descKey] = stepText(step, form, questionsOnly);
  const canNext = ready && canAdvance(step, form, today) && !saving && !uncertain;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const offerSave = shouldOfferSaveAsPackage({
    canUse: canUseLibrary,
    questions: form.questions,
    packageId: form.packageId,
    packageQuestions,
  });

  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    let keepLocked = false;
    try {
      if (questionsOnly) {
        const result = await setAssessment({ ...buildSetAssessmentBody(questionsFor.id, form), groupId });
        if (result.error) {
          setError(result.error.message || t("mzw_questions_save_failed"));
          return;
        }
        keepLocked = true;
        toast.success(t("mzw_questions_saved"));
        onClose();
        return;
      }
      const result = await createMuzakere(buildCreateBody(groupId, form));
      if (result.error) {
        if (result.error.status === "FETCH_ERROR") {
          keepLocked = true;
          setUncertain(true);
          return;
        }
        setError(result.error.message || t("mzw_save_failed"));
        return;
      }
      keepLocked = true;
      const count = result.data?.data?.muzakeres?.length || 1;
      toast.success(count > 1 ? t("mzw_saved_series", { n: count }) : t("mzw_saved"));
      onClose();
    } finally {
      if (!keepLocked) inFlight.current = false;
    }
  };

  const next = () => {
    if (!canNext) return;
    if (isLast) {
      save();
      return;
    }
    if (step === "questions") setForm(singleIfQuestions);
    setError(null);
    setStepIndex((i) => i + 1);
  };
  const back = () => {
    if (saving) return;
    setError(null);
    if (stepIndex === 0) onClose();
    else setStepIndex((i) => i - 1);
  };

  const saveAsPackage = async (title) => {
    const questions = form.questions;
    const result = await createPackage({
      title,
      description: form.description.trim() || undefined,
      questions: questions.map(toPayload),
    });
    const pkg = result.data?.data?.package;
    if (result.error || !pkg?.id) throw new Error(result.error?.message || t("mzq_package_save_failed"));
    setForm((f) => ({ ...f, packageId: pkg.id }));
    setPackageTitle(pkg.title || title);
    setPackageQuestions(questions);
    setPackageSaved(true);
    setSaveOpen(false);
  };

  const issue = step === "questions" ? questionsIssue(form.questions) : null;

  const renderQuestions = () => (
    <QuestionsSection
      questions={form.questions}
      onChange={(questions) => {
        update({ questions });
        setPackageSaved(false);
      }}
      disabled={saving}
      packageTitle={form.packageId ? packageTitle : null}
      onRemovePackage={() => {
        update({ packageId: null, questions: [] });
        setPackageTitle(null);
        setPackageQuestions(null);
        setPackageSaved(false);
      }}
      onPickPackage={canUseLibrary ? () => setPickerOpen(true) : null}
      onSaveAsPackage={offerSave ? () => setSaveOpen(true) : null}
      packageSaved={packageSaved}
    />
  );

  const renderInfo = () => (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className={`flex justify-between text-xs ${cls.muted}`}>
          <span>{t("mzw_title_label")}</span>
          <span>{`${form.title.length}/${MUZAKERE_TITLE_MAX}`}</span>
        </span>
        <input
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && next()}
          maxLength={MUZAKERE_TITLE_MAX}
          autoFocus
          disabled={saving}
          className={inputCls}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={`flex justify-between text-xs ${cls.muted}`}>
          <span>{t("mzw_description_label")}</span>
          <span>{`${form.description.length}/${MUZAKERE_DESCRIPTION_MAX}`}</span>
        </span>
        <textarea
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
          maxLength={MUZAKERE_DESCRIPTION_MAX}
          rows={4}
          disabled={saving}
          className={inputCls}
        />
      </label>
    </div>
  );

  const renderRepeat = () => (
    <div className="flex gap-2" role="radiogroup" aria-label={t("mzw_repeat")}>
      {[
        ["none", "mzw_once"],
        ["weekly", "mzw_weekly"],
        ["monthly", "mzw_monthly"],
      ].map(([value, key]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={form.repeat === value}
          onClick={() => setForm((f) => setRepeat(f, value))}
          className={`flex-1 py-2.5 ${chipCls(form.repeat === value)}`}
        >
          {t(key)}
        </button>
      ))}
    </div>
  );

  const renderCount = () => {
    const limit = REPEAT_LIMITS[form.repeat];
    return (
      <div className="flex items-center justify-between gap-3 py-2">
        <span className={`text-sm ${cls.title}`}>{t("mzw_count")}</span>
        <div className="flex items-center gap-3">
          <IconButton
            size="small"
            aria-label={t("mzw_count_less")}
            onClick={() => update({ count: Math.max(limit.min, form.count - 1) })}
            disabled={saving || form.count <= limit.min}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <span className={`w-8 text-center text-base ${cls.title}`} aria-live="polite">
            {form.count}
          </span>
          <IconButton
            size="small"
            aria-label={t("mzw_count_more")}
            onClick={() => update({ count: Math.min(limit.max, form.count + 1) })}
            disabled={saving || form.count >= limit.max}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  };

  const renderWhen = () => {
    if (form.repeat === "none" || form.questions.length > 0) {
      return (
        <div className="flex flex-col gap-3">
          <input
            type="date"
            value={form.date}
            min={minMuzakereDate(today)}
            onChange={(e) => update({ date: e.target.value })}
            aria-label={t("mzw_date")}
            disabled={saving}
            className={`${inputCls} max-w-[240px]`}
          />
          {form.date && canAdvance("when", form, today) ? (
            <p className={`text-sm ${cls.title}`}>{longDate(form.date)}</p>
          ) : form.date ? (
            <p className={`text-sm ${cls.danger}`}>{t("mzw_date_too_early")}</p>
          ) : null}
        </div>
      );
    }
    const weekly = form.repeat === "weekly";
    return (
      <div className="flex flex-col gap-3">
        {weekly ? (
          <div className="grid grid-cols-7 gap-1.5" role="radiogroup" aria-label={t("mzw_weekday")}>
            {WEEKDAYS.map(([iso, label]) => (
              <button
                key={iso}
                type="button"
                role="radio"
                aria-checked={weekdayOf(form.date) === iso}
                onClick={() => update({ date: nextWeekday(iso, today) })}
                className={`py-2 ${chipCls(weekdayOf(form.date) === iso)}`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1.5" role="radiogroup" aria-label={t("mzw_monthday")}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <button
                  key={day}
                  type="button"
                  role="radio"
                  aria-checked={monthDayOf(form.date) === day}
                  onClick={() => update({ date: nextMonthDay(day, today) })}
                  className={`py-1.5 ${chipCls(monthDayOf(form.date) === day)}`}
                >
                  {day}
                </button>
              ))}
            </div>
            <p className={`text-xs ${cls.muted}`}>{t("mzw_month_clamp_hint")}</p>
          </>
        )}
        {renderCount()}
        {form.date ? (
          <p className={`flex items-center gap-2 text-sm ${cls.title}`}>
            <RepeatIcon fontSize="small" className={cls.accent} />
            {repeatSummary(form, t)}
          </p>
        ) : null}
      </div>
    );
  };

  const summaryRow = (label, value) => (
    <li className={`flex flex-col gap-0.5 py-2.5 ${cls.row}`}>
      <span className={`text-xs ${cls.muted}`}>{label}</span>
      <span className={`text-sm break-words whitespace-pre-line ${cls.title}`}>{value}</span>
    </li>
  );

  const renderSummary = () => {
    const dates = repeatDates(form.date, form.repeat, form.count);
    const repeating = form.repeat !== "none" && form.questions.length === 0;
    return (
      <ul className="border-t border-terakkiBorder dark:border-white/[0.14]">
        {summaryRow(t("mzw_title_label"), form.title.trim())}
        {form.description.trim() ? summaryRow(t("mzw_description_short"), form.description.trim()) : null}
        {summaryRow(
          t("mzw_date"),
          repeating ? `${repeatSummary(form, t)}\n${dates.map(shortDate).join(", ")}` : longDate(form.date)
        )}
        {summaryRow(
          t("mzw_questions_short"),
          form.questions.length
            ? [`${form.questions.length} ${t("assess_question_word")}`, packageTitle].filter(Boolean).join(" · ")
            : t("mzw_no_questions")
        )}
        <li className={`py-2.5 text-xs ${cls.muted}`}>
          {t(repeating ? "mzw_notify_series_hint" : "mzw_notify_hint", { n: dates.length })}
        </li>
      </ul>
    );
  };

  let body;
  if (!ready) {
    body = loadFailed ? (
      <p className={`text-sm ${cls.danger}`}>{t("mzw_questions_load_failed")}</p>
    ) : (
      <div className="flex justify-center py-10">
        <CircularProgress size={24} />
      </div>
    );
  } else if (uncertain) {
    body = <p className={`text-sm ${cls.danger}`}>{t("mzw_uncertain")}</p>;
  } else if (step === "questions") body = renderQuestions();
  else if (step === "info") body = renderInfo();
  else if (step === "repeat") body = renderRepeat();
  else if (step === "when") body = renderWhen();
  else body = renderSummary();

  const footer = (
    <div className="flex flex-col gap-2">
      {error ? (
        <p role="alert" className={`text-sm ${cls.danger}`}>
          {error}
        </p>
      ) : issue && form.questions.length > 0 ? (
        <p className={`text-xs ${cls.danger}`}>{`${issue.index + 1}. ${t(issueText(issue.code))}`}</p>
      ) : null}
      {uncertain ? (
        <SoftButton tone="soft" fullWidth onClick={onClose}>
          {t("mzw_back_to_group")}
        </SoftButton>
      ) : (
        <div className="flex gap-2">
          <SoftButton tone={stepIndex === 0 ? "cancel" : "soft"} fullWidth onClick={back} disabled={saving}>
            {stepIndex === 0 ? t("muzakere_cancel") : t("back")}
          </SoftButton>
          <SoftButton
            tone={isLast ? "success" : "primary"}
            fullWidth
            onClick={next}
            disabled={!canNext}
            endIcon={saving ? null : isLast ? <CheckIcon /> : <ChevronRightIcon />}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : isLast ? t("save") : t("next")}
          </SoftButton>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={saving ? undefined : onClose}
        width="sm"
        title={t(questionsOnly ? "mzw_edit_questions_title" : "mzw_add_title")}
        subtitle={questionsOnly ? questionsFor?.title : undefined}
        titleIcon={<ForumOutlinedIcon fontSize="small" />}
        footer={footer}
      >
        <div className="flex flex-col gap-4 p-5">
          {steps.length > 1 ? (
            <div className="flex justify-center gap-1.5" aria-hidden>
              {steps.map((key, i) => (
                <span
                  key={key}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIndex
                      ? "w-5 bg-terakkiPrimaryDark dark:bg-[#8FC9CA]"
                      : i < stepIndex
                        ? "w-1.5 bg-terakkiBrand dark:bg-white/45"
                        : "w-1.5 bg-terakkiBrandMist dark:bg-white/15"
                  }`}
                />
              ))}
            </div>
          ) : null}
          <div>
            <h3 className={`text-base font-medium ${cls.title}`}>{t(titleKey)}</h3>
            <p className={`text-sm ${cls.muted}`}>{t(descKey)}</p>
          </div>
          {body}
        </div>
      </Drawer>
      <PackagePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(pkg) => {
          setForm((f) => applyPackage(f, pkg));
          setPackageTitle(pkg.title);
          setPackageQuestions(cloneQuestions(pkg.questions));
          setPackageSaved(false);
          setPickerOpen(false);
        }}
      />
      <SaveAsPackageDialog
        open={saveOpen}
        initialTitle={form.title.trim()}
        onClose={() => setSaveOpen(false)}
        onSave={saveAsPackage}
      />
    </>
  );
}
